<template>
  <teleport to="body">
    <div v-if="visible" class="share-card-mask" @click.self="onClose">
      <div class="share-card-dialog">
        <div class="share-card-head">
          <span class="share-card-title"><i class="ri-share-line"></i> 分享卡片</span>
          <a class="share-card-close" @click="onClose"><i class="ri-close-line"></i></a>
        </div>

        <div class="share-card-preview">
          <img v-if="imgUrl" :src="imgUrl" alt="分享卡片预览" />
          <div v-else class="share-card-loading">生成中…</div>
        </div>

        <div class="share-card-actions">
          <button class="share-btn share-btn-primary" :disabled="!imgUrl" @click="download">
            <i class="ri-download-line"></i> 下载 PNG
          </button>
          <button class="share-btn share-btn-ghost" :disabled="!imgUrl || !canClipboard" @click="copy">
            <i class="ri-clipboard-line"></i> {{ copyText }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import aiPush from '../api/aiPush'

const W = 1200
const H = 630

// 亮色 / 暗色两套配色（对齐 style.css 设计 token）
const PALETTES = {
  light: {
    bg: '#FAFAFA',
    card: '#FFFFFF',
    text: '#18181B',
    text2: '#52525B',
    text3: '#A1A1AA',
    accent: '#0D9488',
    accentSoft: '#CCFBF1',
    accentText: '#115E59',
    border: '#E4E4E7',
    heat: ['#EBEDF0', '#9CE3D9', '#4CCFBE', '#14B8A6', '#0F766E']
  },
  dark: {
    bg: '#0B0D0F',
    card: '#14171A',
    text: '#E8EAED',
    text2: '#9AA0A6',
    text3: '#6B7280',
    accent: '#2DD4BF',
    accentSoft: 'rgba(45,212,191,.14)',
    accentText: '#5EEAD4',
    border: '#272B30',
    heat: ['#1E2226', 'rgba(45,212,191,.25)', 'rgba(45,212,191,.45)', 'rgba(45,212,191,.68)', '#2DD4BF']
  }
}

const FONT_STACK = '-apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif'

// 热力图强度分级（与 HomePage.levelOf 保持一致）
function levelOf (words) {
  if (!words) return 0
  if (words < 600) return 1
  if (words < 1400) return 2
  if (words < 2400) return 3
  return 4
}

function formatYmd (d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'ShareCard',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '' },
    pushDate: { type: String, default: '' },
    topicType: { type: String, default: '' }
  },
  emits: ['close'],
  data () {
    return {
      imgUrl: '',
      blob: null,
      copyText: '复制图片',
      canClipboard: typeof navigator !== 'undefined' &&
        !!(navigator.clipboard && typeof ClipboardItem !== 'undefined')
    }
  },
  watch: {
    visible (v) {
      if (v) this.generate()
      else this.cleanup()
    }
  },
  beforeUnmount () {
    this.cleanup()
  },
  methods: {
    onClose () {
      this.$emit('close')
    },
    cleanup () {
      if (this.imgUrl) {
        URL.revokeObjectURL(this.imgUrl)
        this.imgUrl = ''
      }
      this.blob = null
      this.copyText = '复制图片'
    },
    async generate () {
      this.cleanup()
      try {
        // 拉取近一年数据用于绘制迷你热力图（失败则降级为无热力图）
        let yearList = []
        try {
          const end = formatYmd(new Date())
          const start = new Date()
          start.setFullYear(start.getFullYear() - 1)
          const { data } = await aiPush.list({ startDate: formatYmd(start), endDate: end, page: 1, size: 400 })
          yearList = data || []
        } catch (e) {
          yearList = []
        }

        const isDark = document.documentElement.dataset.theme === 'dark'
        const palette = isDark ? PALETTES.dark : PALETTES.light
        const canvas = drawCard({
          title: this.title,
          pushDate: this.pushDate,
          topicType: this.topicType,
          yearList,
          palette
        })
        this.blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
        if (!this.blob) throw new Error('toBlob failed')
        this.imgUrl = URL.createObjectURL(this.blob)
      } catch (e) {
        console.error('生成分享卡片失败', e)
        this.cleanup()
      }
    },
    download () {
      if (!this.imgUrl) return
      const a = document.createElement('a')
      a.href = this.imgUrl
      a.download = `aid-${this.pushDate || 'share'}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    async copy () {
      if (!this.blob || !this.canClipboard) return
      try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': this.blob })])
        this.copyText = '已复制'
        setTimeout(() => { this.copyText = '复制图片' }, 1500)
      } catch (e) {
        this.copyText = '复制失败'
        setTimeout(() => { this.copyText = '复制图片' }, 1500)
      }
    }
  }
}

// ── Canvas 绘制 ─────────────────────────────

function drawCard ({ title, pushDate, topicType, yearList, palette }) {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')

  // 背景
  ctx.fillStyle = palette.bg
  ctx.fillRect(0, 0, W, H)

  // 顶部左侧：装饰竖条 + 站点名
  ctx.fillStyle = palette.accent
  ctx.fillRect(60, 64, 6, 36)
  ctx.fillStyle = palette.text
  ctx.font = `bold 30px ${FONT_STACK}`
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'
  ctx.fillText('AI 学习笔记', 82, 82)

  // 顶部右侧：日期
  ctx.font = `24px ${FONT_STACK}`
  ctx.fillStyle = palette.text3
  ctx.textAlign = 'right'
  ctx.fillText(formatDisplayDate(pushDate), W - 60, 82)
  ctx.textAlign = 'left'

  // 中部：笔记标题（最多 2 行，超出省略）
  ctx.font = `bold 56px ${FONT_STACK}`
  ctx.fillStyle = palette.text
  const titleY = drawWrappedText(ctx, title || '无标题', 60, 180, W - 120, 76, 2)

  // 标题下方：主题 pill
  if (topicType) {
    drawPill(ctx, topicType, 60, titleY + 32, palette)
  }

  // 右下角：迷你热力图（近 12 周）
  drawHeatmap(ctx, yearList, W - 60, H - 60, palette)

  // 左下角：slogan
  ctx.font = `22px ${FONT_STACK}`
  ctx.fillStyle = palette.text3
  ctx.textBaseline = 'alphabetic'
  ctx.fillText('每天一篇 AI 学习笔记 · dawnsstar.top', 60, H - 50)

  return canvas
}

/** 自动换行绘制文本，返回最后一行 baseline y 坐标 */
function drawWrappedText (ctx, text, x, y, maxWidth, lineHeight, maxLines) {
  const chars = String(text).split('')
  let line = ''
  let lineCount = 0
  let curY = y

  for (let i = 0; i < chars.length; i++) {
    const test = line + chars[i]
    if (ctx.measureText(test).width > maxWidth && line) {
      lineCount++
      if (lineCount >= maxLines) {
        // 超出最大行数：最后一行尾部加省略号
        let truncated = line
        while (ctx.measureText(truncated + '…').width > maxWidth && truncated.length > 0) {
          truncated = truncated.slice(0, -1)
        }
        ctx.fillText(truncated + '…', x, curY)
        return curY
      }
      ctx.fillText(line, x, curY)
      curY += lineHeight
      line = chars[i]
    } else {
      line = test
    }
  }
  if (line) {
    ctx.fillText(line, x, curY)
  }
  return curY
}

/** 绘制主题 pill（圆角矩形 + 文字），返回 pill 底部 y 坐标 */
function drawPill (ctx, text, x, y, palette) {
  ctx.font = `22px ${FONT_STACK}`
  const paddingX = 18
  const paddingY = 10
  const textWidth = ctx.measureText(text).width
  const w = textWidth + paddingX * 2
  const h = 22 + paddingY * 2
  const radius = h / 2

  // 背景
  ctx.fillStyle = palette.accentSoft
  roundRect(ctx, x, y, w, h, radius)
  ctx.fill()

  // 文字
  ctx.fillStyle = palette.accentText
  ctx.textBaseline = 'middle'
  ctx.fillText(text, x + paddingX, y + h / 2 + 1)

  return y + h
}

/** 绘制近 12 周迷你热力图（右下角锚点：rightX/bottomY 为右下角坐标） */
function drawHeatmap (ctx, yearList, rightX, bottomY, palette) {
  const cellSize = 14
  const gap = 4
  const weeks = 12
  const days = 7
  const totalW = weeks * (cellSize + gap) - gap
  const totalH = days * (cellSize + gap) - gap
  const startX = rightX - totalW
  const startY = bottomY - totalH - 30 // 顶部预留标题

  // 标题
  ctx.font = `18px ${FONT_STACK}`
  ctx.fillStyle = palette.text3
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'right'
  ctx.fillText('近 12 周', rightX, startY - 10)
  ctx.textAlign = 'left'

  // 构建日期 → 字数映射
  const map = {}
  ;(yearList || []).forEach(d => {
    map[d.push_date] = Number(d.word_count) || 0
  })

  // 计算起点：今天往前推 (weeks * 7 - 1) 天，对齐到周一
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = new Date(today)
  start.setDate(start.getDate() - (weeks * 7 - 1))
  const dow = (start.getDay() + 6) % 7 // 转为周一=0
  start.setDate(start.getDate() - dow)

  // 绘制
  const cur = new Date(start)
  for (let w = 0; w < weeks; w++) {
    for (let d = 0; d < days; d++) {
      const ymd = formatYmd(cur)
      const words = map[ymd] || 0
      const level = levelOf(words)
      // 未来日期不绘制
      if (cur.getTime() <= today.getTime()) {
        ctx.fillStyle = palette.heat[level]
        const x = startX + w * (cellSize + gap)
        const y = startY + d * (cellSize + gap)
        roundRect(ctx, x, y, cellSize, cellSize, 3)
        ctx.fill()
      }
      cur.setDate(cur.getDate() + 1)
    }
  }
}

function roundRect (ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

/** 把 yyyy-MM-dd 格式化为「2026年9月18日」 */
function formatDisplayDate (ymd) {
  if (!ymd) return ''
  const parts = String(ymd).split('-')
  if (parts.length !== 3) return ymd
  return `${Number(parts[0])}年${Number(parts[1])}月${Number(parts[2])}日`
}
</script>
